import React, { useState, useEffect } from "react";
import { useAuth, getAuthToken } from "../context/AuthContext";
import {
  FileText,
  FileSpreadsheet,
  Download,
  Printer,
  Shield,
  Search,
  Filter,
  Users,
  GraduationCap,
  Award,
  Layers,
  CalendarCheck,
  Target,
  TrendingUp,
  History,
  Building,
  CheckCircle,
  AlertTriangle,
  RotateCcw,
  Sparkles,
  ChevronRight
} from "lucide-react";

export const ReportsCenterPage: React.FC = () => {
  const { user, fetchWithAuth } = useAuth();
  const [activeCategory, setActiveCategory] = useState<string>("school-executive");
  const [loading, setLoading] = useState<boolean>(true);

  // Filter states
  const [boardFilter, setBoardFilter] = useState<string>("");
  const [classFilter, setClassFilter] = useState<string>("1");
  const [classList, setClassList] = useState<Array<{ id: number; name: string }>>([]);
  const [boardsList, setBoardsList] = useState<Array<{ id: number; name: string; short_name: string }>>([]);
  const [sectionFilter, setSectionFilter] = useState<string>("");
  const [selectedStudentId, setSelectedStudentId] = useState<string>("1");

  const defaultClasses = [
    { id: 3, name: "Class 6" },
    { id: 4, name: "Class 7" },
    { id: 5, name: "Class 8" },
    { id: 6, name: "Class 9" },
    { id: 1, name: "Class 10" },
    { id: 7, name: "Class 11" },
    { id: 2, name: "Class 12" },
  ];

  const defaultBoards = [
    { id: 1, name: "Haryana Board of School Education", short_name: "HBSE" },
    { id: 2, name: "Central Board of Secondary Education", short_name: "CBSE" },
  ];

  useEffect(() => {
    fetch("/api/classes")
      .then(r => r.json())
      .then(d => {
        if (d.success && Array.isArray(d.classes) && d.classes.length > 0) {
          setClassList(d.classes);
        }
      })
      .catch(() => {});

    fetch("/api/boards")
      .then(r => r.json())
      .then(d => {
        if (d.success && Array.isArray(d.boards) && d.boards.length > 0) {
          setBoardsList(d.boards);
        }
      })
      .catch(() => {});
  }, []);

  // Report data states
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [studentReport, setStudentReport] = useState<any>(null);
  const [classReport, setClassReport] = useState<any>(null);
  const [subjectReport, setSubjectReport] = useState<any>(null);
  const [boardReport, setBoardReport] = useState<any>(null);
  const [assessmentReport, setAssessmentReport] = useState<any>(null);
  const [satReport, setSatReport] = useState<any>(null);
  const [attendanceReport, setAttendanceReport] = useState<any>(null);
  const [interventionReport, setInterventionReport] = useState<any>(null);
  const [executiveReport, setExecutiveReport] = useState<any>(null);
  const [customReport, setCustomReport] = useState<any>(null);
  const [reportHistory, setReportHistory] = useState<any[]>([]);

  // Custom report builder column selection
  const [selectedColumns, setSelectedColumns] = useState<string[]>([
    "srn", "student_name", "board", "class", "section", "overall_score", "attendance"
  ]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      if (activeCategory === "dashboard") {
        const res = await fetchWithAuth("/api/reports/dashboard").then(r => r.json());
        if (res.success) setDashboardData(res);
      } else if (activeCategory === "student") {
        const res = await fetchWithAuth(`/api/reports/student/${selectedStudentId || 1}`).then(r => r.json());
        if (res.success) setStudentReport(res);
      } else if (activeCategory === "class") {
        const res = await fetchWithAuth(`/api/reports/class?class_id=${classFilter}&board_id=${boardFilter}`).then(r => r.json());
        if (res.success) setClassReport(res);
      } else if (activeCategory === "subject") {
        const res = await fetchWithAuth("/api/reports/subject").then(r => r.json());
        if (res.success) setSubjectReport(res.subjects);
      } else if (activeCategory === "board") {
        const res = await fetchWithAuth("/api/reports/board").then(r => r.json());
        if (res.success) setBoardReport(res);
      } else if (activeCategory === "assessment") {
        const res = await fetchWithAuth("/api/reports/assessment").then(r => r.json());
        if (res.success) setAssessmentReport(res.assessments);
      } else if (activeCategory === "sat") {
        const res = await fetchWithAuth("/api/reports/sat").then(r => r.json());
        if (res.success) setSatReport(res.sats);
      } else if (activeCategory === "attendance") {
        const res = await fetchWithAuth("/api/reports/attendance").then(r => r.json());
        if (res.success) setAttendanceReport(res);
      } else if (activeCategory === "intervention") {
        const res = await fetchWithAuth("/api/reports/intervention").then(r => r.json());
        if (res.success) setInterventionReport(res);
      } else if (activeCategory === "school-executive") {
        const res = await fetchWithAuth("/api/reports/school-executive").then(r => r.json());
        if (res.success) setExecutiveReport(res);
      } else if (activeCategory === "custom") {
        const res = await fetchWithAuth(`/api/reports/custom?board_id=${boardFilter}&class_id=${classFilter}&section=${sectionFilter}&columns=${selectedColumns.join(",")}`).then(r => r.json());
        if (res.success) setCustomReport(res.data);
      } else if (activeCategory === "history") {
        const res = await fetchWithAuth("/api/reports/history").then(r => r.json());
        if (res.success) setReportHistory(res.history);
      }
    } catch (err) {
      console.error("Error loading report data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [activeCategory, boardFilter, classFilter, sectionFilter, selectedStudentId, selectedColumns]);

  const handleExport = (format: "csv" | "xlsx" | "pdf") => {
    window.open(`/api/reports/export/${format}?report_type=${activeCategory}`, "_blank");
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Official Government Header */}
      <div className="bg-gradient-to-r from-[#0F2C59] via-[#1E3A8A] to-[#0F2C59] text-white p-6 rounded-2xl shadow-lg border-b-4 border-amber-500">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-wider">
              <Shield className="w-4 h-4" /> District School Education Department, Nuh, Haryana
            </div>
            <h1 className="text-2xl font-black mt-1 tracking-tight flex items-center gap-2">
              <FileText className="w-6 h-6 text-amber-400" />
              Reports & Government Export Center
            </h1>
            <p className="text-xs text-blue-100 mt-1 max-w-2xl leading-relaxed">
              Official standardized reporting and data export system (Academic Session 2026–27).
              Download verified government dossiers, student portfolios, attendance summaries, and analytics in PDF, Excel, and CSV.
            </p>
          </div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              onClick={() => handleExport("pdf")}
              className="px-3.5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition"
            >
              <FileText className="w-4 h-4" /> Export PDF
            </button>
            <button
              onClick={() => handleExport("xlsx")}
              className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition"
            >
              <FileSpreadsheet className="w-4 h-4" /> Export Excel
            </button>
            <button
              onClick={() => handleExport("csv")}
              className="px-3.5 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button
              onClick={() => window.print()}
              className="px-3.5 py-2 bg-slate-800 hover:bg-slate-900 text-amber-300 rounded-xl text-xs font-bold flex items-center gap-1.5 border border-slate-700 shadow-sm transition"
            >
              <Printer className="w-4 h-4" /> Print Report
            </button>
          </div>
        </div>

        {/* Categories Bar */}
        <div className="flex items-center gap-2 mt-6 overflow-x-auto pt-2 border-t border-blue-800/60">
          {[
            { id: "school-executive", label: "Executive Report", icon: Building },
            { id: "student", label: "Student Profile", icon: GraduationCap },
            { id: "class", label: "Class Performance", icon: Layers },
            { id: "subject", label: "Subject Ranking", icon: Award },
            { id: "board", label: "Board Analysis", icon: Shield },
            { id: "assessment", label: "Assessments", icon: FileText },
            { id: "sat", label: "SAT Exams", icon: Sparkles },
            { id: "attendance", label: "Attendance", icon: CalendarCheck },
            { id: "intervention", label: "Interventions", icon: Target },
            { id: "custom", label: "Custom Builder", icon: Filter },
            { id: "history", label: "Report History", icon: History }
          ].map(cat => {
            const Icon = cat.icon;
            const active = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition whitespace-nowrap ${
                  active
                    ? "bg-amber-500 text-slate-950 font-black shadow-md"
                    : "bg-blue-900/40 text-blue-100 hover:bg-blue-800/60 hover:text-white"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {cat.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white p-4 rounded-2xl shadow-xs border border-slate-200 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700">
            <Filter className="w-4 h-4 text-blue-700" /> Report Filters:
          </div>
          <div className="px-3 py-1.5 bg-slate-100 rounded-lg text-xs font-bold text-slate-800">
            Session: 2026–27
          </div>
          <select
            value={boardFilter}
            onChange={e => setBoardFilter(e.target.value)}
            className="px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700"
          >
            <option value="">All Boards (HBSE & CBSE)</option>
            {(boardsList.length > 0 ? boardsList : defaultBoards).map(b => (
              <option key={b.id} value={String(b.id)}>
                {b.short_name} ({b.name})
              </option>
            ))}
          </select>
          <select
            value={classFilter}
            onChange={e => setClassFilter(e.target.value)}
            className="px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700"
          >
            <option value="">All Classes (Class 6–12)</option>
            {(classList.length > 0 ? classList : defaultClasses).map(c => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>

          {activeCategory === "student" && (
            <select
              value={selectedStudentId}
              onChange={e => setSelectedStudentId(e.target.value)}
              className="px-3 py-1.5 bg-blue-50 border border-blue-300 text-blue-900 rounded-lg text-xs font-bold"
            >
              <option value="1">TEST-HBSE-10-001 (Aarav Sharma)</option>
              <option value="2">TEST-CBSE-10-001 (Pooja Verma)</option>
              <option value="3">TEST-HBSE-12-001 (Rohan Khan)</option>
              <option value="4">TEST-CBSE-12-001 (Kavita Rao)</option>
            </select>
          )}
        </div>

        <button
          onClick={fetchReportData}
          className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold flex items-center gap-1.5 transition"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* VIEW: SCHOOL EXECUTIVE PERFORMANCE REPORT */}
      {activeCategory === "school-executive" && executiveReport && (
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-md space-y-6">
          <div className="text-center border-b-2 border-slate-900 pb-4">
            <div className="text-sm font-black tracking-widest uppercase text-slate-900">HARYANA GOVERNMENT</div>
            <div className="text-xs font-bold text-slate-700 uppercase">Department of School Education</div>
            <div className="text-base font-black text-blue-950 mt-1">District School Education Department, Nuh, Haryana</div>
            <div className="text-xs font-bold text-amber-700 mt-0.5">Board Exam Ki Taiyari & Improvement • Academic Session 2026–27</div>
            <div className="text-[11px] text-slate-500 mt-2">
              Comprehensive School Executive Performance Audit Dossier (Sections A to P)
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="text-xs font-bold text-slate-500 uppercase">A. School Information</div>
              <div className="text-sm font-bold text-slate-900 mt-1">{executiveReport.sections.A_school_information.name}</div>
              <div className="text-xs text-slate-600">Code: {executiveReport.sections.A_school_information.code}</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="text-xs font-bold text-slate-500 uppercase">B. Student Strength</div>
              <div className="text-sm font-bold text-slate-900 mt-1">{executiveReport.sections.B_student_strength.total} Enrolled Candidates</div>
              <div className="text-xs text-slate-600">Class 10: 2 • Class 12: 2</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="text-xs font-bold text-slate-500 uppercase">C. Faculty Strength</div>
              <div className="text-sm font-bold text-slate-900 mt-1">{executiveReport.sections.C_teacher_strength.total} Assigned Teachers</div>
              <div className="text-xs text-slate-600">Status: All Active</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50/60 rounded-xl border border-blue-200">
              <div className="text-xs font-bold text-blue-900 uppercase">D. HBSE Performance Summary</div>
              <div className="text-xs text-slate-700 mt-2 space-y-1">
                <div>Enrolled: <strong>{executiveReport.sections.D_hbse_summary.enrolled} Candidates</strong></div>
                <div>Assessment Avg: <strong>{executiveReport.sections.D_hbse_summary.assessment_average}</strong></div>
                <div>SAT Exam Avg: <strong>{executiveReport.sections.D_hbse_summary.sat_average}</strong></div>
              </div>
            </div>
            <div className="p-4 bg-blue-50/60 rounded-xl border border-blue-200">
              <div className="text-xs font-bold text-blue-900 uppercase">E. CBSE Performance Summary</div>
              <div className="text-xs text-slate-700 mt-2 space-y-1">
                <div>Enrolled: <strong>{executiveReport.sections.E_cbse_summary.enrolled} Candidates</strong></div>
                <div>Assessment Avg: <strong>{executiveReport.sections.E_cbse_summary.assessment_average}</strong></div>
                <div>SAT Exam Avg: <strong>{executiveReport.sections.E_cbse_summary.sat_average}</strong></div>
              </div>
            </div>
          </div>

          <div className="p-5 bg-amber-50/60 rounded-xl border border-amber-200 space-y-2">
            <div className="text-xs font-bold text-amber-900 uppercase">P. Academic Recommendations (Rule-Based)</div>
            <ul className="list-disc list-inside text-xs text-amber-950 space-y-1 font-medium">
              {executiveReport.sections.P_recommendations.map((rec: string, i: number) => (
                <li key={i}>{rec}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* VIEW: STUDENT PROFILE REPORT */}
      {activeCategory === "student" && studentReport && (
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-md space-y-6">
          <div className="flex items-center justify-between border-b pb-4">
            <div>
              <h2 className="text-lg font-black text-slate-900">{studentReport.student.student_name}</h2>
              <div className="text-xs text-slate-500 font-medium">
                SRN: <span className="font-mono font-bold text-blue-900">{studentReport.student.srn}</span> • S/o {studentReport.student.father_name}
              </div>
            </div>
            <div className="text-right">
              <span className="px-3 py-1 bg-blue-100 text-blue-900 rounded-lg text-xs font-bold">
                {studentReport.student.board_name} • {studentReport.student.class_name}-{studentReport.student.section}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4 p-4 bg-slate-50 rounded-xl text-xs font-semibold text-slate-700">
            <div>Overall Score: <span className="font-bold text-slate-900">{studentReport.student.overall_percentage}%</span></div>
            <div>Assessment Avg: <span className="font-bold text-blue-900">{studentReport.student.assessment_average}%</span></div>
            <div>SAT Avg: <span className="font-bold text-emerald-700">{studentReport.student.sat_average}%</span></div>
            <div>Attendance: <span className="font-bold text-purple-700">{studentReport.student.attendance_percentage}%</span></div>
          </div>

          <div>
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2">Subject Performance Table</h3>
            <table className="w-full text-left text-xs border border-slate-200 border-collapse">
              <thead className="bg-slate-100 font-bold text-slate-700">
                <tr>
                  <th className="p-2.5 border">Subject</th>
                  <th className="p-2.5 border text-center">Latest Score</th>
                  <th className="p-2.5 border text-center">Previous</th>
                  <th className="p-2.5 border text-center">Average %</th>
                  <th className="p-2.5 border text-center">Change</th>
                  <th className="p-2.5 border text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {studentReport.subject_table?.map((sub: any) => (
                  <tr key={sub.subject_id} className="hover:bg-slate-50">
                    <td className="p-2.5 border font-bold text-slate-900">{sub.subject_name}</td>
                    <td className="p-2.5 border text-center font-bold">{sub.latest_score}/100</td>
                    <td className="p-2.5 border text-center text-slate-500">{sub.previous_score}</td>
                    <td className="p-2.5 border text-center font-black text-blue-900">{sub.average_percentage}%</td>
                    <td className="p-2.5 border text-center font-bold text-emerald-600">{sub.change}</td>
                    <td className="p-2.5 border text-center">
                      <span className="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-bold">{sub.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* VIEW: CLASS PERFORMANCE REPORT */}
      {activeCategory === "class" && classReport && (
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-md space-y-6">
          <div className="flex items-center justify-between border-b pb-4">
            <div>
              <h2 className="text-lg font-black text-slate-900">{classReport.class_name} Performance Report</h2>
              <div className="text-xs text-slate-500">Total Evaluated Candidates: {classReport.total_students}</div>
            </div>
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-900">
              <strong>Top Performer:</strong> {classReport.top_performer?.student_name} ({classReport.top_performer?.percentage}%)
            </div>
          </div>

          {/* Distribution Grid */}
          <div className="grid grid-cols-6 gap-3">
            {Object.entries(classReport.distribution || {}).map(([bracket, count]) => (
              <div key={bracket} className="p-3 bg-slate-50 border rounded-xl text-center">
                <div className="text-[10px] font-bold text-slate-400">{bracket}</div>
                <div className="text-lg font-black text-slate-900 mt-1">{String(count)}</div>
              </div>
            ))}
          </div>

          <table className="w-full text-left text-xs border border-slate-200 border-collapse">
            <thead className="bg-slate-100 font-bold text-slate-700">
              <tr>
                <th className="p-2.5 border">Subject</th>
                <th className="p-2.5 border text-center">Average %</th>
                <th className="p-2.5 border text-center">Highest %</th>
                <th className="p-2.5 border text-center">Lowest %</th>
              </tr>
            </thead>
            <tbody>
              {classReport.subject_table?.map((sub: any, i: number) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="p-2.5 border font-bold text-slate-900">{sub.subject_name}</td>
                  <td className="p-2.5 border text-center font-black text-blue-900">{sub.average_percentage}%</td>
                  <td className="p-2.5 border text-center font-bold text-emerald-700">{sub.highest_percentage}%</td>
                  <td className="p-2.5 border text-center font-bold text-amber-700">{sub.lowest_percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW: CUSTOM REPORT BUILDER */}
      {activeCategory === "custom" && (
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-md space-y-6">
          <div>
            <h2 className="text-base font-black text-slate-900">Custom Report Builder</h2>
            <p className="text-xs text-slate-500 mt-0.5">Select required columns and filters to generate tailored government reports.</p>
          </div>

          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
            <div className="text-xs font-bold text-slate-700">Select Columns to Include:</div>
            <div className="flex flex-wrap gap-4 text-xs font-semibold text-slate-700">
              {[
                { id: "srn", label: "Student SRN" },
                { id: "student_name", label: "Student Name" },
                { id: "father_name", label: "Father Name" },
                { id: "board", label: "Board (HBSE/CBSE)" },
                { id: "class", label: "Class & Section" },
                { id: "overall_score", label: "Overall Score %" },
                { id: "attendance", label: "Attendance %" },
                { id: "status", label: "Enrollment Status" }
              ].map(col => (
                <label key={col.id} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(col.id)}
                    onChange={e => {
                      if (e.target.checked) setSelectedColumns([...selectedColumns, col.id]);
                      else setSelectedColumns(selectedColumns.filter(c => c !== col.id));
                    }}
                    className="rounded text-blue-600"
                  />
                  {col.label}
                </label>
              ))}
            </div>
          </div>

          {/* Live Preview Table */}
          <div>
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2">
              Preview Results ({customReport ? customReport.length : 0} Records)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border border-slate-200 border-collapse">
                <thead className="bg-slate-100 font-bold text-slate-700">
                  <tr>
                    {selectedColumns.map(col => (
                      <th key={col} className="p-2.5 border uppercase text-[11px]">{col.replace("_", " ")}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {customReport?.map((row: any, i: number) => (
                    <tr key={i} className="hover:bg-slate-50">
                      {selectedColumns.map(col => (
                        <td key={col} className="p-2.5 border font-medium">
                          {row[col] || "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* VIEW: REPORT HISTORY */}
      {activeCategory === "history" && (
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-md space-y-4">
          <h2 className="text-base font-black text-slate-900">Official Report Generation History</h2>
          <table className="w-full text-left text-xs border border-slate-200 border-collapse">
            <thead className="bg-slate-100 font-bold text-slate-700">
              <tr>
                <th className="p-2.5 border">Report Type</th>
                <th className="p-2.5 border text-center">Format</th>
                <th className="p-2.5 border">Generated By</th>
                <th className="p-2.5 border text-center">Records</th>
                <th className="p-2.5 border text-right">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {reportHistory.map(h => (
                <tr key={h.id} className="hover:bg-slate-50">
                  <td className="p-2.5 border font-bold text-slate-900">{h.report_type}</td>
                  <td className="p-2.5 border text-center font-bold">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-900 rounded text-[10px]">{h.format}</span>
                  </td>
                  <td className="p-2.5 border font-medium text-slate-700">{h.generated_by_user || "System Admin"} ({h.user_role || "admin"})</td>
                  <td className="p-2.5 border text-center font-semibold">{h.record_count}</td>
                  <td className="p-2.5 border text-right text-slate-500">{new Date(h.created_at).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
